import { api } from "../../api";

/**
 * Super-admin Leads inbox API slice.
 *
 * Wraps `/api/v1/admin/leads/*` endpoints. The list query is shaped
 * to plug directly into the shared `useFetchAll` hook so the Leads
 * page can use the same TableLayout + Filters + global search
 * machinery as Jobs/Manifests/Payouts.
 *
 * All endpoints require a super-admin token; the underlying API
 * returns 403 to anyone else.
 */
const leadsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    listLeads: builder.query({
      query: ({
        page = 1,
        limit = 25,
        status,
        q,
        searchTerm,
        search,
        ...rest
      } = {}) => {
        const params = new URLSearchParams();
        params.set("page", String(page));
        params.set("limit", String(limit));

        // Status comes in as a single value or a comma-separated list
        // (TableLayout's multi-select filter joins with commas).
        if (status) params.set("status", String(status));

        // useFetchAll always sends `searchTerm` for global search; we
        // map it to the API's canonical `q` param.
        const term = q || searchTerm;
        if (term) params.set("q", String(term));
        if (search === true) params.set("search", "true");

        // Pass-through for any future filter ids without code changes.
        Object.entries(rest).forEach(([key, value]) => {
          if (value === undefined || value === null || value === "") return;
          params.set(key, String(value));
        });

        return {
          url: `/admin/leads?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => {
        const data = response?.data || {};
        const leads = Array.isArray(data.leads)
          ? data.leads
          : Array.isArray(data.docs)
            ? data.docs
            : [];
        const total = data.total ?? data.totalDocs ?? leads.length;
        const totalPages = data.totalPages ?? 1;
        const page = data.page ?? 1;
        return {
          leads,
          total,
          totalPages,
          page,
          nextPage: data.nextPage ?? page + 1,
          hasNext: data.hasNext ?? page < totalPages,
        };
      },
      providesTags: (result) => {
        const docs = result?.leads || [];
        return [
          { type: "Leads", id: "LIST" },
          ...docs.map((lead) => ({ type: "Leads", id: lead._id })),
        ];
      },
    }),

    getLead: builder.query({
      query: ({ id }) => ({
        url: `/admin/leads/${id}`,
        method: "GET",
      }),
      transformResponse: (response) => response?.data || null,
      providesTags: (_result, _error, { id }) => [{ type: "Leads", id }],
    }),

    updateLeadStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/admin/leads/${id}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Leads", id },
        { type: "Leads", id: "LIST" },
      ],
    }),

    updateLeadAssignee: builder.mutation({
      query: ({ id, assignedTo }) => ({
        url: `/admin/leads/${id}/assignee`,
        method: "PATCH",
        body: { assignedTo },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Leads", id },
        { type: "Leads", id: "LIST" },
      ],
    }),

    addLeadNote: builder.mutation({
      query: ({ id, body }) => ({
        url: `/admin/leads/${id}/notes`,
        method: "POST",
        body: { body },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Leads", id },
        { type: "Leads", id: "LIST" },
      ],
    }),

    /**
     * Converts a won lead into a Customer using the same onboarding
     * pipeline as Super Admin → Create customer.
     */
    createCustomerFromLead: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/admin/leads/${id}/create-customer`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Leads", id },
        { type: "Leads", id: "LIST" },
        { type: "Customers", id: "LIST" },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListLeadsQuery,
  useGetLeadQuery,
  useUpdateLeadStatusMutation,
  useUpdateLeadAssigneeMutation,
  useAddLeadNoteMutation,
  useCreateCustomerFromLeadMutation,
} = leadsApi;

export default leadsApi;
